<template>
  <div>
    <data-table-header
      :title="$t('admin.orderReport')"
      :date-label="$t('admin.reportDate')"
      :toggle-label="$t('transactionReports.confirmedOrders')"
      has-toggle
      :toggle-value="confirmed"
      has-date-presets
      :initial-start-date="startDate"
      :initial-end-date="endDateRaw"
      @startDate="setStartDate"
      @endDate="setEndDate"
      @endDateRaw="setEndDateRaw"
      @toggle="setConfirmed"
    />
    <v-container class="d-flex align-center py-0">
      <v-btn-toggle
        v-model="resolutionFilter"
        mandatory
        dense
      >
        <v-btn
          :value="null"
          small
        >
          {{ $t('common.all') }}
        </v-btn>
        <v-btn
          value="Open"
          small
        >
          {{ $t('transactionReports.resolution.open') }}
        </v-btn>
        <v-btn
          value="Closed"
          small
        >
          {{ $t('transactionReports.resolution.closed') }}
        </v-btn>
      </v-btn-toggle>
      <ekitabu-select
        id="fulfillmentStatusFilter"
        v-model="fulfillmentStatusFilter"
        :items="statusFilterOptions"
        :label="$t('transactionReports.filterByStatus')"
        class="ml-4 status-filter-select"
      />
      <v-spacer />
      <download-order-report-button :order-data="currentOrderData" />
    </v-container>
    <OrdersTable
      :start-date="startDate"
      :end-date="endDate"
      :confirmed="confirmed"
      :resolution="resolutionFilter"
      :fulfillment-status="fulfillmentStatusFilter || null"
      :custom-headers="headerOverride"
      :from-page="fromRoutes.orderReport"
      @data-loaded="currentOrderData = $event"
    />
  </div>
</template>

<script>
import DataTableHeader from "@/components/tables/DataTableHeader.vue";
import OrdersTable from "@/components/tables/OrdersTable.vue";
import DownloadOrderReportButton from "@/components/DownloadOrderReportButton.vue";
import { FROM_ROUTES } from "@/utils/Const";

// In-memory cache so the filters survive navigating to an order's details and
// back, within the same SPA session. `endDateRaw` is the raw picker value (for
// restoring the input); `endDate` is the +1-day query bound. All filters that
// feed the pagination cache key are cached so the table restores to the same
// page rather than a default-filter page.
const filterStateCache = {
  startDate: "",
  endDate: "",
  endDateRaw: "",
  confirmed: true,
  resolutionFilter: null,
  fulfillmentStatusFilter: "",
};

export default {
  name: "OrderReport",
  components: {
    DataTableHeader,
    OrdersTable,
    DownloadOrderReportButton,
  },
  data() {
    return {
      startDate: filterStateCache.startDate,
      endDate: filterStateCache.endDate,
      endDateRaw: filterStateCache.endDateRaw,
      confirmed: filterStateCache.confirmed,
      resolutionFilter: filterStateCache.resolutionFilter,
      fulfillmentStatusFilter: filterStateCache.fulfillmentStatusFilter,
      currentOrderData: [],
    };
  },
  computed: {
    fromRoutes() {
      return FROM_ROUTES;
    },
    statusFilterOptions() {
      const statuses = [
        'new',
        'processing',
        'waiting_for_stock',
        'packaged',
        'dispatched',
        'delivered',
        'unable_to_deliver',
        'confirmed',
      ];
      return [
        { text: this.$t('common.all'), value: '' },
        ...statuses.map((s) => ({
          text: this.$t('transactionReports.statuses.' + s),
          value: s,
        })),
      ];
    },
    headerOverride() {
      return [
        {
          text: this.$t("transactionReports.orderId"),
          value: "id",
          class: "column-header",
          sortable: false,
        },
        {
          text: this.$t("transactionReports.orderNumber"),
          value: "orderNumber",
          class: "column-header",
        },
        {
          text: this.$t("transactionReports.date"),
          value: "updatedAt",
          class: "column-header",
        },
        {
          text: this.$t("transactionReports.customer"),
          value: "customerEmail",
          class: "column-header",
        },
        {
          text: this.$t("transactionReports.event"),
          value: "event.name",
          class: "column-header",
        },
        {
          text: this.$t("transactionReports.resolutionStatus"),
          value: "resolution",
          class: "column-header",
          sortable: false,
        },
        {
          text: this.$t("transactionReports.fulfillmentStatus"),
          value: "fulfillmentStatus",
          class: "column-header",
          sortable: false,
        },
        {
          text: this.$t("transactionReports.quantity"),
          value: "quantity",
          class: "column-header",
          sortable: false,
        },
        {
          text: this.$t("transactionReports.totalPrice"),
          value: "totalPriceSet",
          class: "column-header",
        },
      ];
    },
  },
  watch: {
    resolutionFilter(value) {
      filterStateCache.resolutionFilter = value;
    },
    fulfillmentStatusFilter(value) {
      filterStateCache.fulfillmentStatusFilter = value;
    },
  },
  methods: {
    setStartDate(value) {
      this.startDate = value;
      filterStateCache.startDate = value;
    },
    setEndDate(value) {
      this.endDate = value;
      filterStateCache.endDate = value;
    },
    setEndDateRaw(value) {
      this.endDateRaw = value;
      filterStateCache.endDateRaw = value;
    },
    setConfirmed(value) {
      this.confirmed = value;
      filterStateCache.confirmed = value;
    },
  },
};
</script>

<style scoped>
.status-filter-select {
  max-width: 250px;
}
</style>
